import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';

const paragraphContainerStyle: React.CSSProperties = {
  width: '100%',
  paddingBottom: '12px'
};

interface Props {
  paragraphText: string;
  maxRows?: number;
  elipsisClosedText?: string;
  elipsisOpenText?: string;
  expanded?: boolean;
}

const ExpandButton = styled.button`
  all: unset; /* reset default browser button styles */
  display: inline; /* put icon + text side by side */
  align-items: center; /* vertically center contents */
  cursor: pointer; /* show pointer on hover */

  color: var(--productPrimary);
  text-decoration: underline;
  white-space: nowrap;
  padding-left: 5px;
  &:focus {
    outline: 2px solid #007bff; /* customize focus ring */
    outline-offset: 2px;
  }
`;

const TypographyExpandEllipsis = ({
  paragraphText,
  maxRows = 3,
  elipsisClosedText = 'View more',
  elipsisOpenText = 'View less',
  expanded
}: Props) => {
  // Detail: Ant design version 4.x does not support collapses on Typography.Paragraph elipsis, The logic in this replicates the described behaviour of the antd component in version 5.x
  // TODO: Upgrade to antd 5.x when available and replace this component with the antd component
  const [isExpanded, setIsExpanded] = useState(expanded);
  const [isEllipsis, setIsEllipsis] = useState(false);
  const [visibleText, setVisibleText] = useState(paragraphText);
  const [isMeasuring, setIsMeasuring] = useState(true);
  const paragraphRef = useRef<HTMLParagraphElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const calculateMaxChars = useCallback(() => {
    // Calculator the Number of characters that fit in the paragraph * maxRows
    if (!measureRef.current || !paragraphRef.current || !containerRef.current) return;
    setIsMeasuring(true);
    // Temporarily hide the container to prevent flickering and grab measurements
    containerRef.current.style.visibility = 'hidden';
    paragraphRef.current.style.display = 'block';
    measureRef.current.style.display = 'block';
    const lineHeight = parseInt(window.getComputedStyle(paragraphRef.current).lineHeight);
    const maxContainerHeight = lineHeight * maxRows;

    let low = 0;
    let high = paragraphText.length;
    let bestFit = '';

    // Perform binary search to find the maximum number of characters that fit within the measured height
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const testText = paragraphText.slice(0, mid);
      measureRef.current.textContent = testText;

      if (measureRef.current.clientHeight <= maxContainerHeight) {
        low = mid + 1;
        bestFit = testText;
      } else {
        high = mid - 1;
      }
    }
    // Remove excess text from end of best fit and add ellipsis
    if (bestFit.length < paragraphText.length) {
      bestFit = bestFit.slice(0, bestFit.length - elipsisClosedText.length - 10) + '\u2026';
    }
    setVisibleText(bestFit);
    setIsEllipsis(bestFit.length < paragraphText.length);
    // Unhide the container and hide the measurement div
    paragraphRef.current.style.display = 'none';
    measureRef.current.style.display = 'none';
    containerRef.current.style.visibility = 'visible';
    setIsMeasuring(false);
  }, [maxRows, paragraphText, elipsisClosedText.length]);

  useEffect(() => {
    calculateMaxChars();
    window.addEventListener('resize', calculateMaxChars);
    return () => {
      window.removeEventListener('resize', calculateMaxChars);
    };
  }, [paragraphText, maxRows, calculateMaxChars]);

  return (
    <div style={paragraphContainerStyle} ref={containerRef}>
      {/* Hidden div to measure text height with ellipsis */}
      <div
        ref={paragraphRef}
        style={{
          display: 'none',
          overflow: 'hidden',
          visibility: 'hidden',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: maxRows,
          zIndex: -1,
          width: '100%',
          whiteSpace: 'normal'
        }}
      >
        {paragraphText}
      </div>
      {/* Hidden div for binary search height calculation */}
      <div
        ref={measureRef}
        style={{
          display: 'none',
          visibility: 'hidden',
          zIndex: -1,
          width: '100%',
          whiteSpace: 'normal'
        }}
      >
        {paragraphText}
      </div>
      {/* Display the actual paragraph text */}
      <div
        style={{
          visibility: isMeasuring ? 'hidden' : 'visible',
          display: '-webkit-box',
          overflow: 'hidden',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: isExpanded ? 'unset' : maxRows,
          whiteSpace: 'normal',
          width: '100%',
          fontSize: '14px'
        }}
      >
        {isExpanded ? paragraphText : visibleText}
        {isEllipsis && (
          <ExpandButton
            onClick={() => {
              setIsExpanded(!isExpanded);
            }}
            data-testid="expand-button"
          >
            {isExpanded ? elipsisOpenText : elipsisClosedText}
          </ExpandButton>
        )}
      </div>
    </div>
  );
};

export default TypographyExpandEllipsis;
